import { ColDef, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, blueButtonClass, grayButtonClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR1020_R01Store } from "./Store_PUR1020_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR1020_R01HeaderDto, defaultPUR1020_R01SuppliedDto, PUR1020_R01HeaderDto, PUR1020_R01SuppliedDto } from "./Dto_PUR1020_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1020_R01-supplied-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PUR1020_R01SuppliedDto> | null>;
	handleWMS1130ProcessClick: () => void;
}

export const SuppliedTable_PUR1020_R01 = (props: Props) => {

	const { gridRef, handleWMS1130ProcessClick } = props;

    const { 
		selectedPUR1020_R01,
        PUR1020_R01SuppliedList, setPUR1020_R01SuppliedList,
        deletedPUR1020_R01SuppliedList, setDeletedPUR1020_R01SuppliedList, addDeletedPUR1020_R01SuppliedList, removeDeletedPUR1020_R01SuppliedList, 
        selectedPUR1020_R01SuppliedList, setSelectedPUR1020_R01SuppliedList, addSelectedPUR1020_R01SuppliedList, removeSelectedPUR1020_R01SuppliedList,
        originalPUR1020_R01SuppliedList, setOriginalPUR1020_R01SuppliedList,
		suppliedColumnState, setSuppliedColumnState,
		suppliedScrollTop, setSuppliedScrollTop,
    } = usePUR1020_R01Store() as any;

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
    } = useGridSettingContent<PUR1020_R01SuppliedDto>({
		gridRef,
		key: 'childItemcd',
        gridData: PUR1020_R01SuppliedList,
        defaultGridData: defaultPUR1020_R01SuppliedDto,
        selectedRow: selectedPUR1020_R01SuppliedList,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		childItemcd: '품목코드',
		childItemnm: '자재명',
		materialIntype: '규격',
		childItemUsage: '소요량',
		orderPrice: '자재단가',
		moqQty: 'MOQ',
		orderQty: '주문량',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'childItemcd')(params), []);

    const baseColumnDefs: ColDef<PUR1020_R01SuppliedDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'childItemcd', field: 'childItemcd', width: 180,
			cellStyle: (p) => getCellStyle(p, 'childItemcd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '자재명', colId: 'childItemnm', field: 'childItemnm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'childItemnm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'materialIntype', field: 'materialIntype', width: 60,
			cellStyle: (p) => getCellStyle(p, 'materialIntype'),
			headerClass: aggridHeaderClass,
			cellRenderer: (params: { data?: PUR1020_R01SuppliedDto }) => {
				if (params.data?.materialIntype === 'SUPPLIED') {
					return <div>사급</div>;
				}
			},
		},
		{
			headerName: '소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 70,
			cellStyle: (p) => getCellStyle(p, 'childItemUsage'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '총소요량', colId: 'totalQty', field: 'totalQty', width: 80,
			cellStyle: (p) => getCellStyle(p, 'totalQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: 'MOQ', colId: 'moqQty', field: 'moqQty', width: 90,
			cellStyle: (p) => getCellStyle(p, 'moqQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '현재고총량', colId: 'stockQty', field: 'stockQty', width: 90,
			cellStyle: (p) => getCellStyle(p, 'stockQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '사급품필요량', colId: 'orderQty', field: 'orderQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '자품목단위', colId: 'childItemUnit', field: 'childItemUnit', width: 90,
			cellStyle: (p) => getCellStyle(p, 'childItemUnit'),
			headerClass: aggridHeaderClass,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
    ], [getCellStyle, unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

    const columnDefs: ColDef<PUR1020_R01SuppliedDto>[] = useMemo(() => {
		if (suppliedColumnState === null) {
			return baseColumnDefs;
		}
        return GridCustomColumnDefs<PUR1020_R01SuppliedDto>({
			baseColumnDefs,
			columnState: suppliedColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [suppliedColumnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
    const handleRowClick = useCallback((e: RowClickedEvent<PUR1020_R01SuppliedDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - childItemcd 사용
		const currentKey = rowData.childItemcd;
        const selectedKey = selectedPUR1020_R01SuppliedList;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
            setSelectedPUR1020_R01SuppliedList([]);
		} else {
            setSelectedPUR1020_R01SuppliedList([currentKey]);
		}
    }, [selectedPUR1020_R01SuppliedList, setSelectedPUR1020_R01SuppliedList]);

	
    const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
        childItemcd: PUR1020_R01SuppliedList.length.toLocaleString() + "건",
		childItemUsage: '',
		orderPrice: '',
		orderQty: PUR1020_R01SuppliedList.reduce((sum: number, item: PUR1020_R01SuppliedDto) => sum + (Number(item.orderQty) || 0), 0),
		totalQty: PUR1020_R01SuppliedList.reduce((sum: number, item: PUR1020_R01SuppliedDto) => sum + (Number(item.totalQty) || 0), 0),
		stockQty: PUR1020_R01SuppliedList.reduce((sum: number, item: PUR1020_R01SuppliedDto) => sum + (Number(item.stockQty) || 0), 0),
		moqQty: '',
    }]), [PUR1020_R01SuppliedList]);

	useEffect(() => { ensureSelectedRow(); }, [PUR1020_R01SuppliedList, selectedPUR1020_R01SuppliedList]);

	const isDisabled = useMemo(() => {
		return PUR1020_R01SuppliedList.length < 1;
	}, [PUR1020_R01SuppliedList]);

	return (
		
        <GridContextWrapper<PUR1020_R01SuppliedDto>
        	enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
            initSelected={() => setSelectedPUR1020_R01SuppliedList([])}
			defaultRowData={defaultPUR1020_R01SuppliedDto}
			baseColumnDefs={suppliedColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setSuppliedColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
				
			<div className="flex justify-end">
				
				<button
						onClick={handleWMS1130ProcessClick}
						className={`${ 
									isDisabled ? grayButtonClass : blueButtonClass
								} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px] mb-[4px]`}
						disabled={isDisabled}
						title={isDisabled ? "사급품 입고등록할 품목을 선택해주세요" : "선택된 품목을 사급품 입고등록으로 전환"}
					>
						사급품 입고등록
				</button>
			</div>
            <Grid_Touch<PUR1020_R01SuppliedDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
                rowClassRules={createRowClassRules<PUR1020_R01SuppliedDto>({
					idKey: 'childItemcd',
                    selectedList: selectedPUR1020_R01SuppliedList,
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리지)	
				setColumnState={setSuppliedColumnState}			//컬럼 상태 저장
				scrollTop={suppliedScrollTop}
				setScrollTop={setSuppliedScrollTop}
			/>
		</GridContextWrapper>
	)
}
